import { createContext, useContext } from 'react';

import { useQuery } from 'react-query';

import { State, useState, createState } from '@hookstate/core';

import { indexStudioEquipmentStatus } from '@services/queries/studios';

import EquipmentStatus from '@models/EquipmentStatus.model';

import { NavigationContext } from '@layout/navigation/navigation';
import { CustomStatusLoading } from './CustomStatus.loading';
import { CustomStatusError } from './CustomStatus.error';
import { CustomStatusRaw } from './CustomStatus.raw';

import {
  CreateEquipmentStatus,
  DeleteEquipmentStatus,
  EditEquipmentStatus,
} from './components';

export type SortCustomStatusTypes =
  | 'equipment_status.name'
  | 'equipment_status.description'
  | 'equipment_status.category'
  | undefined;

export type DirectionCustomStatustypes = 'ASC' | 'DESC' | undefined;

interface ICustomStatusContext {
  openCreateDialog: () => void;
  openUpdateDialog: (status: EquipmentStatus) => void;
  openDeleteDialog: (status: EquipmentStatus) => void;
  equipmentStatus: EquipmentStatus;
  openDelete: State<boolean>;
  search: State<string>;
  sort: State<SortCustomStatusTypes>;
  direction: State<DirectionCustomStatustypes>;
}

export const CustomStatusContext = createContext<ICustomStatusContext>({
  openCreateDialog: () => {},
  openUpdateDialog: () => {},
  openDeleteDialog: () => {},
  equipmentStatus: EquipmentStatus.createEmpty(),
  openDelete: createState<boolean>(false),
  search: createState<string>(''),
  sort: createState<SortCustomStatusTypes>(undefined),
  direction: createState<DirectionCustomStatustypes>(undefined),
});

const CustomStatusQuery = () => {
  const { studioId } = useContext(NavigationContext);

  const openCreateDialog = useState<boolean>(false);
  const openEditDialog = useState<boolean>(false);
  const openDeleteDialog = useState<boolean>(false);
  const search = useState<string>('');
  const sort = useState<SortCustomStatusTypes>(undefined);
  const direction = useState<DirectionCustomStatustypes>(undefined);
  const hasData = useState<boolean>(false);

  const equipmentStatus = useState<EquipmentStatus>(
    EquipmentStatus.createEmpty(),
  );

  const { data, isLoading, isError } = useQuery(
    [
      'studios',
      studioId,
      'customStatus',
      search.get(),
      sort.get(),
      direction.get(),
    ],
    () =>
      indexStudioEquipmentStatus({
        id: studioId,
        search: search.get(),
        sort: sort.get(),
        direction: direction.get(),
      }),
    {
      onSuccess: () => {
        hasData.set(true);
      },
    },
  );

  if (isLoading && !hasData.get()) {
    return <CustomStatusLoading />;
  }

  if (isError) {
    return <CustomStatusError />;
  }

  return (
    <CustomStatusContext.Provider
      value={{
        openDeleteDialog: status => {
          equipmentStatus.set(status);
          openDeleteDialog.set(true);
        },
        openUpdateDialog: status => {
          equipmentStatus.set(status);
          openEditDialog.set(true);
        },
        openCreateDialog: () => openCreateDialog.set(true),
        equipmentStatus: equipmentStatus.get(),
        openDelete: openDeleteDialog,
        search,
        sort,
        direction,
      }}
    >
      <CustomStatusRaw equipmentStatuses={data} />

      <CreateEquipmentStatus open={openCreateDialog} />
      {openEditDialog.get() && (
        <EditEquipmentStatus
          open={openEditDialog}
          equipmentStatus={equipmentStatus.get()}
        />
      )}

      <DeleteEquipmentStatus />
    </CustomStatusContext.Provider>
  );
};

export { CustomStatusQuery };
